import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/shared/ui';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/lib/constants/routes';

// Logo is now in public folder
const medicrewLogo = '/assets/images/brand/medicrew_blue_logo.png';

export default function SignupComplete(): React.ReactElement {
    const { t } = useTranslation('auth');
    const navigate = useNavigate();
    const { user } = useAuth();

    const handleLogin = () => {
        navigate(ROUTES.AUTH.LOGIN);
    };

    /** 비로그인 시 인증 API를 쓸 수 없으므로 로그인 후 `/verify-email`으로 보냄 (Login의 state.from) */
    const handleEmailVerification = () => {
        if (user?.isAuthenticated) {
            navigate(ROUTES.AUTH.EMAIL_VERIFICATION);
            return;
        }
        navigate(ROUTES.AUTH.LOGIN, {
            state: {
                from: { pathname: ROUTES.AUTH.EMAIL_VERIFICATION },
            },
        });
    };

    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-8">
            {/* Complete Card */}
            <div className="w-full max-w-[460px] bg-white rounded-3xl p-14 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.08)] border border-gray-100">
                {/* Logo */}
                <div className="text-center mb-12">
                    <Link to="/" className="inline-block">
                        <img src={medicrewLogo} alt="Medicrew" className="h-7 w-auto" />
                    </Link>
                </div>

                {/* Success Icon */}
                <div className="flex justify-center mb-8">
                    <div className="text-6xl animate-bounce-subtle">
                        <span>✨</span>
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-2xl font-semibold text-brand-500 text-center mb-8">
                    {t('signupComplete.pageTitle')}
                </h1>

                {/* Message */}
                <div className="text-center space-y-4 mb-10">
                    <p className="text-gray-700 text-base">
                        {t('signupComplete.guestMessage')}
                    </p>
                    <p className="text-gray-700 text-base">
                        {t('signupComplete.loginPrompt')} <br />
                        {t('signupComplete.emailVerifyNote')}
                    </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-3">
                    <Button
                        onClick={handleLogin}
                        className="w-full rounded-2xl text-lg h-12"
                        variant="primary"
                    >
                        {t('signupComplete.loginButton')}
                    </Button>
                    <Button
                        onClick={handleEmailVerification}
                        className="w-full rounded-2xl text-lg h-12 border-2 border-brand-500 text-brand-500 hover:bg-gray-50"
                        variant="secondary"
                    >
                        {t('signupComplete.emailVerifyButton')}
                    </Button>
                </div>
            </div>

            {/* Powered By */}
            <div className="mt-10 flex justify-center items-center gap-2">
                <span className="text-xs text-gray-400">Powered by</span>
                <span className="text-sm font-bold text-gray-500">NEWBASE</span>
            </div>

            {/* Footer Rules Links */}
            <div className="w-full max-w-[460px] flex justify-between items-center text-xs text-gray-400 mt-8">
                {/* Left: Home Button */}
                <Link to="/" className="flex items-center gap-2 hover:text-gray-600 transition-colors font-medium">
                    ← {t('homeLink')}
                </Link>

                {/* Right: Terms & Privacy */}
                <div className="flex gap-6">
                    <Link to={ROUTES.COMMON.TERMS} className="hover:underline text-inherit">{t('terms')}</Link>
                    <Link to={ROUTES.COMMON.PRIVACY} className="hover:underline text-inherit">{t('privacy')}</Link>
                </div>
            </div>

            {/* Animations */}
            <style>{`
                @keyframes bounce-subtle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-10px); }
                }
                .animate-bounce-subtle {
                    animation: bounce-subtle 2s ease-in-out infinite;
                    display: inline-block;
                }
            `}</style>
        </div>
    );
}
